<!--
  NFT Detail Modal Component
  
  This component displays detailed information about a single NFT in a modal overlay.
  Users can view NFT metadata, attributes, estimated values, and confirm their selection
  for use in the calculator.
  
  Features:
  - Displays NFT image, title, collection, and estimated value
  - Shows NFT attributes in a grid layout
  - Provides confirmation modal for NFT selection
  - Handles missing NFT data gracefully
  - Debug mode support for development
  - Responsive design for mobile and desktop
  
  Props:
  - modelValue: Controls modal visibility
  - nft: NFT object containing metadata and details
  - debugMode: Enables debug logging and UI elements
  
  Events:
  - update:modelValue: Emitted when modal visibility changes
  - close: Emitted when modal is closed
  - confirm: Emitted when user confirms NFT selection
-->

<template>
  <Teleport to="body">
    <!-- Main Modal -->
    <div v-if="modalVisible" class="modal" @click="handleModalClick" :data-debug="debugMode">
      <!-- Debug info -->
      <div v-if="debugMode" style="position: fixed; top: 10px; left: 10px; background: rgba(0,0,0,0.9); color: #00ff00; padding: 10px; border-radius: 5px; font-family: monospace; font-size: 12px; z-index: 100001;">
        Modal Visible: {{ modalVisible }}<br>
        NFT: {{ nft ? 'Present' : 'None' }}<br>
        Selected NFT: {{ selectedNFT ? 'Present' : 'None' }}
      </div>
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>NFT Details</h3>
          <button class="modal-close" @click="closeModal">&times;</button>
        </div>
        <div class="nft-details" v-if="nft">
          <div class="nft-preview">
            <div class="nft-preview-image">
              <img 
                :src="getNFTImage(nft)" 
                :alt="nft.name || nft.title || 'NFT Preview'"
              />
            </div>
            <div class="nft-preview-info">
              <h4>{{ nft.name || nft.title || 'Untitled NFT' }}</h4>
              <p class="collection">{{ nft.contractMetadata?.name || nft.contract?.name || 'Unknown Collection' }}</p>
              <p v-if="nft.estimatedValue?.displayValue" class="value">
                {{ nft.estimatedValue.displayValue }}
              </p>
              <button v-if="nft.estimatedValue?.value" class="btn-use-floor" @click="showConfirmationModal(nft)">
                Use Floor Price
              </button>
            </div>
          </div>
          
          <div class="nft-attributes" v-if="nft.metadata?.attributes?.length">
            <h4>Attributes</h4>
            <div class="attributes-grid">
              <div v-for="(attr, index) in nft.metadata.attributes" :key="index" class="attribute">
                <div class="attribute-trait">{{ attr.trait_type }}</div>
                <div class="attribute-value">{{ attr.value }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="no-nft-selected">
          <p>No NFT selected</p>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showConfirmation" class="modal confirmation-modal" @click="closeConfirmationModal">
      <div class="modal-content confirmation-content" @click.stop>
        <div class="modal-header">
          <h3>Confirm NFT Selection</h3>
          <button class="modal-close confirmation-close" @click="closeConfirmationModal">&times;</button>
        </div>
        <div class="confirmation-body">
          <div class="nft-preview">
            <div class="nft-preview-image">
              <img 
                v-if="selectedNFT?.media?.[0]?.gateway || selectedNFT?.image" 
                :src="getNFTImage(selectedNFT)" 
                :alt="selectedNFT?.name || selectedNFT?.title || 'NFT Preview'"
              />
            </div>
            <div class="nft-preview-details">
              <div class="nft-preview-name">{{ selectedNFT?.name || selectedNFT?.title || 'Unknown NFT' }}</div>
              <div class="nft-preview-collection">{{ selectedNFT?.contractMetadata?.name || 'Unknown Collection' }}</div>
              <div class="nft-preview-value">
                <span v-if="selectedNFT?.estimatedValue?.displayValue">
                  Estimated Value: {{ selectedNFT.estimatedValue.displayValue }}
                </span>
              </div>
            </div>
          </div>
          <div class="nft-details-section">
            <h4>NFT Details:</h4>
            <div class="nft-details-content">
              <div v-if="selectedNFT?.contract?.address" class="detail-item">
                <strong>Contract:</strong> {{ formatAddress(selectedNFT.contract.address) }}
              </div>
              <div v-if="selectedNFT?.id && typeof selectedNFT.id === 'object' && selectedNFT.id.tokenId" class="detail-item">
                <strong>Token ID:</strong> {{ selectedNFT.id.tokenId }}
              </div>
              <div v-if="selectedNFT?.contract?.tokenType" class="detail-item">
                <strong>Type:</strong> {{ selectedNFT.contract.tokenType }}
              </div>
              <div v-if="selectedNFT?.description" class="detail-item">
                <strong>Description:</strong> {{ selectedNFT.description }}
              </div>
              <div v-if="selectedNFT?.estimatedValue?.source" class="detail-item">
                <strong>Value Source:</strong> {{ selectedNFT.estimatedValue.source }}
              </div>
              <div v-if="selectedNFT?.estimatedValue?.confidence" class="detail-item">
                <strong>Confidence:</strong> 
                <span :class="`confidence-${selectedNFT.estimatedValue.confidence}`">
                  {{ selectedNFT.estimatedValue.confidence }}
                </span>
              </div>
            </div>
          </div>
          <div class="confirmation-actions">
            <button class="btn-cancel" @click="closeConfirmationModal">Cancel</button>
            <button class="btn-confirm" @click="confirmSelection">Confirm Selection</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { NFT } from '../types/nft'

// Props
interface Props {
  modelValue?: boolean
  nft?: NFT | null
  debugMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  nft: null,
  debugMode: false
})

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
  confirm: [nft: NFT]
}>()

// Reactive state
const modalVisible = ref(props.modelValue)
const showConfirmation = ref(false)
const selectedNFT = ref<NFT | null>(null)

// Debug function
const debug = (...args: any[]) => {
  if (props.debugMode) {
    console.log('[NFT Detail Modal]', ...args)
  }
}

// Computed
const nft = computed(() => props.nft)

// Watch for modelValue changes
watch(() => props.modelValue, (newValue) => {
  modalVisible.value = newValue
})

// Methods
const closeModal = () => {
  modalVisible.value = false
  emit('update:modelValue', false)
  emit('close')
}

const handleModalClick = (event: Event) => {
  // Close modal when clicking outside the content
  if (event.target === event.currentTarget) {
    closeModal()
  }
}

const showConfirmationModal = (nft: NFT) => {
  selectedNFT.value = nft
  showConfirmation.value = true
}

const closeConfirmationModal = () => {
  showConfirmation.value = false
  selectedNFT.value = null
}

const confirmSelection = () => {
  if (selectedNFT.value) {
    emit('confirm', selectedNFT.value)
    closeConfirmationModal()
    closeModal()
  }
}

const getNFTImage = (nft: NFT): string => {
  if (nft.media?.[0]?.gateway) {
    return nft.media[0].gateway
  }
  if (typeof nft.image === 'string') {
    return nft.image
  }
  if (nft.image?.cachedUrl) {
    return nft.image.cachedUrl
  }
  return ''
}

const formatAddress = (address: string): string => {
  if (!address) return ''
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

// Watch for NFT changes
watch(() => props.nft, (newNFT) => {
  debug('NFT prop changed:', newNFT ? 'present' : 'none')
})
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 8px;
  max-width: 90vw;
  max-height: 90vh;
  width: 600px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--text-primary);
  transition: background-color 0.3s, color 0.3s;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-secondary);
  transition: background-color 0.3s, border-color 0.3s;
}

.modal-header h3 {
  margin: 0;
  color: var(--text-primary);
  transition: color 0.3s;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
}

.modal-close:hover {
  color: var(--text-primary);
}

.nft-details {
  padding: 20px;
  overflow-y: auto;
  max-height: calc(90vh - 80px);
}

.nft-preview {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}

.nft-preview-image {
  width: 200px;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--background-secondary);
  flex-shrink: 0;
}

.nft-preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.nft-preview-info {
  flex: 1;
}

.nft-preview-info h4 {
  margin: 0 0 10px 0;
  font-size: 24px;
  color: var(--text-primary);
  transition: color 0.3s;
}

.collection {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: var(--text-secondary);
  transition: color 0.3s;
}

.value {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--positive);
  transition: color 0.3s;
}

.btn-use-floor {
  padding: 12px 24px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 16px;
  transition: background-color 0.2s;
}

.btn-use-floor:hover {
  background: var(--primary-hover);
}

.nft-attributes {
  margin-top: 30px;
}

.nft-attributes h4 {
  margin: 0 0 15px 0;
  font-size: 20px;
  color: var(--text-primary);
  transition: color 0.3s;
}

.attributes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.attribute {
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 10px;
  background: var(--background-secondary);
  transition: background-color 0.3s, border-color 0.3s;
}

.attribute-trait {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: color 0.3s;
}

.attribute-value {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
  transition: color 0.3s;
}

.no-nft-selected {
  padding: 40px;
  text-align: center;
  color: var(--text-secondary);
  transition: color 0.3s;
}

/* Confirmation Modal Styles */
.confirmation-modal {
  z-index: 100001;
}

.confirmation-content {
  width: 500px;
  max-height: 80vh;
}

.confirmation-body {
  padding: 20px;
  overflow-y: auto;
  max-height: calc(80vh - 80px);
}

.nft-preview {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  padding: 15px;
  background: var(--background-secondary);
  border-radius: 8px;
  transition: background-color 0.3s;
}

.nft-preview-image {
  width: 80px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--background-tertiary);
  flex-shrink: 0;
}

.nft-preview-details {
  flex: 1;
}

.nft-preview-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 5px;
  transition: color 0.3s;
}

.nft-preview-collection {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 5px;
  transition: color 0.3s;
}

.nft-preview-value {
  font-size: 14px;
  color: var(--positive);
  font-weight: 500;
  transition: color 0.3s;
}

.nft-details-section {
  margin-bottom: 20px;
}

.nft-details-section h4 {
  margin: 0 0 10px 0;
  font-size: 16px;
  color: var(--text-primary);
  transition: color 0.3s;
}

.nft-details-content {
  background: var(--background-secondary);
  border-radius: 6px;
  padding: 15px;
  transition: background-color 0.3s;
}

.detail-item {
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--text-primary);
  transition: color 0.3s;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item strong {
  color: var(--text-secondary);
  margin-right: 8px;
  transition: color 0.3s;
}

.confidence-high {
  color: var(--positive);
}

.confidence-medium {
  color: var(--warning);
}

.confidence-low {
  color: var(--error);
}

.confirmation-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn-cancel {
  padding: 10px 20px;
  background: var(--background-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
}

.btn-cancel:hover {
  background: var(--background-tertiary);
}

.btn-confirm {
  padding: 10px 20px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn-confirm:hover {
  background: var(--primary-hover);
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-content {
    width: 95vw;
    max-height: 95vh;
  }
  
  .nft-preview {
    flex-direction: column;
    gap: 15px;
  }
  
  .nft-preview-image {
    width: 100%;
    height: 200px;
  }
  
  .attributes-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  
  .confirmation-content {
    width: 95vw;
  }
  
  .confirmation-actions {
    flex-direction: column;
  }
}
</style> 